import { KeyboardEventHandler, useState, useEffect, useCallback } from 'react';
import { KeyboardKeys } from '@/utils/Keyboard';

type UseDropdownKeyboardOptions = {
  isOpen: boolean;
  optionsLength: number;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (index: number) => void;
};

type KeyboardAction = {
  preventDefault: boolean;
  action: () => void;
};

export const useDropdownKeyboard = ({
  isOpen,
  optionsLength,
  onOpen,
  onClose,
  onSelect,
}: UseDropdownKeyboardOptions) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  const moveToNext = useCallback(() => {
    setFocusedIndex(prev => (prev + 1) % optionsLength);
  }, [optionsLength]);

  const moveToPrevious = useCallback(() => {
    setFocusedIndex(prev => (prev - 1 + optionsLength) % optionsLength);
  }, [optionsLength]);

  const moveToNextWithTab = useCallback(() => {
    if (focusedIndex < optionsLength - 1) {
      setFocusedIndex(focusedIndex + 1);
      return true;
    }
    onClose();
    return false;
  }, [focusedIndex, optionsLength, onClose]);

  const moveToPreviousWithTab = useCallback(() => {
    if (focusedIndex > 0) {
      setFocusedIndex(focusedIndex - 1);
      return true;
    }
    onClose();
    return false;
  }, [focusedIndex, onClose]);

  const selectCurrentOption = useCallback(() => {
    if (focusedIndex >= 0 && focusedIndex < optionsLength) {
      onSelect(focusedIndex);
      onClose();
    }
  }, [focusedIndex, optionsLength, onSelect, onClose]);

  const handleClosedDropdown = useCallback(
    (key: string): KeyboardAction | null => {
      if (key === KeyboardKeys.Enter || key === KeyboardKeys.Space) {
        return {
          preventDefault: true,
          action: onOpen,
        };
      }
      return null;
    },
    [onOpen],
  );

  const handleOpenDropdown = useCallback(
    (key: string, shiftKey?: boolean): KeyboardAction | null => {
      switch (key) {
        case KeyboardKeys.Tab:
          return {
            preventDefault: shiftKey
              ? moveToPreviousWithTab()
              : moveToNextWithTab(),
            action: () => {},
          };

        case KeyboardKeys.ArrowDown:
          return {
            preventDefault: true,
            action: moveToNext,
          };

        case KeyboardKeys.ArrowUp:
          return {
            preventDefault: true,
            action: moveToPrevious,
          };

        case KeyboardKeys.Enter:
          return {
            preventDefault: true,
            action: selectCurrentOption,
          };

        case KeyboardKeys.Escape:
          return {
            preventDefault: true,
            action: onClose,
          };

        default:
          return null;
      }
    },
    [
      moveToNext,
      moveToPrevious,
      moveToNextWithTab,
      moveToPreviousWithTab,
      selectCurrentOption,
      onClose,
    ],
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      const keyboardAction = isOpen
        ? handleOpenDropdown(e.key, e.shiftKey)
        : handleClosedDropdown(e.key);

      if (!keyboardAction) {
        return;
      }

      if (keyboardAction.preventDefault) {
        e.preventDefault();
      }
      keyboardAction.action();
    },
    [isOpen, handleOpenDropdown, handleClosedDropdown],
  );

  const setFocusedIndexByMouse = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  return {
    focusedIndex,
    handleKeyDown,
    setFocusedIndexByMouse,
  };
};
